import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { matchApi } from '../../services/api';
import type { Match } from '../../models';
import { getTeamShortName } from '../../lib/team'

interface MatchAutocompleteProps {
  value: Match | null;
  onChange: (value: Match | null) => void;
  error?: boolean;
  helperText?: string;
  seriesId?: number; // Optional series ID to filter matches by series
  disabled?: boolean;
}

const MatchAutocomplete: React.FC<MatchAutocompleteProps> = ({
  value,
  onChange,
  error,
  helperText,
  seriesId,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Match[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Handle input change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [inputValue]);
  
  // Fetch matches only when dropdown is open and search term / series ID changes
  useEffect(() => {
    if (!open) return; // don't fetch unless user has opened the dropdown

    let active = true;

    const fetchMatches = async () => {
      setLoading(true);
      try {
        const params: any = {
          search: searchTerm,
          page_size: 10,
          autocomplete: true,
          // ordering: '-start_date',
        };

        // If seriesId is provided, filter matches by that series
        if (seriesId) {
          params.series = seriesId;
        }

        const response = await matchApi.getAll(params);
        if (active) {
          setOptions(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching matches:', error);
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchMatches();

    return () => {
      active = false;
    };
  }, [open, searchTerm, seriesId]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(_event, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      filterOptions={(options) => options} // Disable client-side filtering
      getOptionLabel={(option) => {
        // Format the label to display relevant match information
        const teams = option.teams?.map(team => getTeamShortName(team)).join(' vs ') || 'TBD';
        const date = new Date(option.start_date).toLocaleDateString();
        return `${teams} (${option.match_format}, ${date}) - ${option.series?.name || ''}`;
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      size='small'
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Match"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MatchAutocomplete;
